/**
 * Onglets « Par caméras » du mega menu Shape.
 * - Survol d'une marque : affiche ses modèles (avec un court délai pour éviter
 *   de changer d'onglet quand la souris traverse la liste en diagonale)
 * - Clavier : flèches haut/bas (desktop) ou gauche/droite (mobile), Début/Fin, comme un tablist ARIA
 * - Clic sur une marque : ouvre la collection de la marque (lien normal, desktop)
 * - Mobile : les marques sont des boutons, un tap affiche leurs modèles
 */
class ShapeMegaTabs extends HTMLElement {
  /** @type {number | undefined} */
  #hoverTimer;

  connectedCallback() {
    this.tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);

    this.tabs.forEach((tab) => {
      tab.addEventListener('pointerenter', this.#onPointerEnter);
      tab.addEventListener('pointerleave', this.#onPointerLeave);
      tab.addEventListener('focus', this.#onFocus);
      tab.addEventListener('keydown', this.#onKeydown);
      if (tab instanceof HTMLButtonElement) tab.addEventListener('click', this.#onClick);
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this.#hoverTimer);
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.select(tab), 90);
  };

  #onPointerLeave = () => {
    window.clearTimeout(this.#hoverTimer);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    this.select(tab);
    tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  /** @param {FocusEvent} event */
  #onFocus = (event) => {
    this.select(/** @type {HTMLElement} */ (event.currentTarget));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const index = this.tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    const last = this.tabs.length - 1;
    const horizontal = this.querySelector('[role="tablist"]')?.getAttribute('aria-orientation') === 'horizontal';
    const nextKey = horizontal ? 'ArrowRight' : 'ArrowDown';
    const previousKey = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const targets = {
      [nextKey]: index === last ? 0 : index + 1,
      [previousKey]: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = targets[/** @type {keyof typeof targets} */ (event.key)];
    if (next === undefined) return;

    event.preventDefault();
    this.tabs[next].focus();
  };

  /** @param {HTMLElement} tab */
  select(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;

      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    });
  }
}

if (!customElements.get('shape-mega-tabs')) {
  customElements.define('shape-mega-tabs', ShapeMegaTabs);
}

/**
 * Ouverture / fermeture du mega menu à la souris, avec « intention de survol ».
 * Horizon ouvre et ferme instantanément au pointerenter / pointerleave, ce qui provoque
 * des ouvertures involontaires (passage rapide sur un lien) et des fermetures en plein
 * survol (sortie d'un pixel entre le lien et le panneau).
 * Ici :
 * - ouverture après un court délai (le pointeur doit « s'arrêter » sur le lien)
 * - passage d'un menu à l'autre avec un délai un peu plus long, annulé si le pointeur
 *   redescend dans le panneau déjà ouvert
 * - fermeture avec une tolérance, annulée si le pointeur revient
 * L'état reste celui d'Horizon (header-menu.activate / deactivate) pour garder
 * l'accessibilité, le clavier et le calcul des hauteurs.
 */
const OPEN_DELAY = 70;
const SWITCH_DELAY = 160;
const CLOSE_DELAY = 280;

/** @type {HTMLElement | null} */
let openItem = null;
/** @type {number | undefined} */
let openTimer;
/** @type {number | undefined} */
let closeTimer;

/** @param {EventTarget | null} target */
const findItem = (target) =>
  target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest('[data-shape-mega-item]')) : null;

/** @param {HTMLElement} item */
const headerMenuOf = (item) =>
  /** @type {(HTMLElement & { activate?: Function, deactivate?: Function }) | null} */ (item.closest('header-menu'));

/** @param {HTMLElement} item */
function open(item) {
  window.clearTimeout(closeTimer);
  if (openItem === item) return;
  if (openItem) close(openItem);
  headerMenuOf(item)?.activate?.({ target: item });
  openItem = item;
}

/** @param {HTMLElement} item */
function close(item) {
  headerMenuOf(item)?.deactivate?.({ target: item, relatedTarget: null, type: 'pointerleave' });
  if (openItem === item) openItem = null;
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;
  const item = findItem(event.target);
  if (!item) return;

  window.clearTimeout(openTimer);

  if (item === openItem) {
    // Retour sur le menu ouvert (lien ou panneau) : on annule toute fermeture ou bascule en attente
    window.clearTimeout(closeTimer);
    return;
  }

  openTimer = window.setTimeout(() => open(item), openItem ? SWITCH_DELAY : OPEN_DELAY);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse') return;
  const item = findItem(event.target);
  if (!item || item.contains(/** @type {Node | null} */ (event.relatedTarget))) return;

  if (item === openItem) {
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (openItem === item) close(item);
    }, CLOSE_DELAY);
  } else {
    // Le pointeur a seulement traversé ce lien : pas d'ouverture
    window.clearTimeout(openTimer);
  }
});

// Un clic ou le clavier ailleurs referme proprement
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openItem) close(openItem);
});

/* ---------- Barre de recherche centrale du header ---------- */

/**
 * Ouvre la recherche : la recherche Shape (assets/shape-search.js) si elle est présente
 * (elle intercepte déjà les clics et le raccourci en amont), sinon celle d'Horizon.
 */
function openSearch() {
  const shapeSearch = /** @type {(HTMLElement & { open?: Function }) | null} */ (document.querySelector('shape-search'));
  if (shapeSearch?.open) {
    shapeSearch.open();
    return;
  }
  const modal = /** @type {(HTMLElement & { showDialog?: Function }) | null} */ (document.getElementById('search-modal'));
  if (modal?.showDialog) {
    modal.showDialog();
  } else {
    // Repli : clic sur le bouton loupe d'origine
    /** @type {HTMLElement | null} */ (document.querySelector('search-button button'))?.click();
  }
}

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('[data-shape-search]')) openSearch();
});

// Raccourci « / » (hors champs de saisie), comme sur la plupart des sites
document.addEventListener('keydown', (event) => {
  if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
  const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
  if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
  if (!document.querySelector('[data-shape-search]')) return;
  event.preventDefault();
  openSearch();
});
